import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ENDPOINTS,
    MaterialCheckModel,
    ODataMaterialAvailabilityResponseModel,
    ODataMaterialCheckModel,
    ODataMatLTextModel,
    OnlineOnlyService,
} from '@evorait/pace-core';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import MaterialAvailabilityModel from './material-availability.model';

@Component({
    selector: 'pace-material-availability',
    templateUrl: './material-availability.component.html',
    styleUrls: ['./material-availability.component.scss'],
})
export class MaterialAvailabilityComponent implements OnInit {
    @Input()
    public model: MaterialAvailabilityModel;

    private readonly globalStorageLocation: string[];

    public responsePartSet: MaterialCheckModel[] = [];
    public isLoading = false;
    public formChildren: FormArray;
    public materialAvailabilityForm: FormGroup;
    public sparePartsCatalogLink: string;
    public readonly showMaterialPricingInfo: boolean;
    public readonly showMaterialTSInfo: boolean;
    public readonly showMaterialNDFInfo: boolean;
    protected readonly faTrashAlt = faTrashAlt;
    public constructor(
        private readonly translate: TranslateService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly appNotificationService: AppNotificationService,
        private readonly configService: ConfigService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.model = new MaterialAvailabilityModel();
        this.globalStorageLocation = this.configService.getGlobalStorageLocation();
        this.sparePartsCatalogLink = this.configService.getSparePartsCatalogLink();
        this.showMaterialPricingInfo = this.clientManager.isNgmAP0();
        this.showMaterialTSInfo = !this.clientManager.isNgmAA();
        this.showMaterialNDFInfo = !this.clientManager.isNgmAA();
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.materialAvailabilityForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
    }

    /**
     * This function adds the material to the checklist
     */
    public addMaterial(): void {
        this.model.addMaterialToPartSet();
    }

    public deleteMaterial(index: number): void {
        this.model.deleteMaterialFromPartSet(index);
    }

    /**
     * This function sends the POST request to actually check the material availability
     */
    public check(): void {
        // check if there was material selected
        if (!this.model.partSet.value.length) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('MATERIAL_REQUEST_NO_SELECTION_TITLE'),
                <string>this.translate.instant('MATERIAL_REQUEST_NO_SELECTION_BODY'),
            );
            return;
        }

        this.isLoading = true;
        let filteredPartSetItem: ODataMaterialCheckModel[];

        this.onlineOnlyService
            .post<ODataMaterialAvailabilityResponseModel>(ENDPOINTS.MatAvailabilitySet, this.model.getOutModel())
            .pipe(
                take(1),
                map((data: ODataMaterialAvailabilityResponseModel) => {
                    if (typeof data.PartSet === 'object' && Array.isArray(data.PartSet.results)) {
                        this.model.partSet.value = [];
                        filteredPartSetItem = data.PartSet.results;
                        if (this.globalStorageLocation.length > 0) {
                            filteredPartSetItem = data.PartSet.results.filter(
                                (part) =>
                                    this.globalStorageLocation.includes(part[ConfigConstants.materialFilterKey]) ||
                                    part.Available === '0',
                            );
                        }
                        this.responsePartSet = this.responsePartSet.concat(
                            this.populateResponsePartSet(filteredPartSetItem, data.LtextSet.results),
                        );
                    }
                }),
                catchError((error) => {
                    this.log.error(`MaterialAvailabilityComponent: check: Error`, error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('MATERIAL_REQUEST_ERROR_TITLE'),
                        `${<string>this.translate.instant('MATERIAL_REQUEST_ERROR_BODY')} ${JSON.stringify(error)}`,
                    );
                    return of(undefined);
                }),
                map(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe();
    }

    /**
     * This function creates MaterialCheckModel objects from list of materials and returns MaterialCheckModel array
     *
     * @param materials the list of materials
     * @param longText the list of long texts
     * @returns the resulting materials
     */
    private populateResponsePartSet(
        materials: ODataMaterialCheckModel[],
        longText: ODataMatLTextModel[],
    ): MaterialCheckModel[] {
        const resultedMaterials: MaterialCheckModel[] = [];
        materials.forEach((result: ODataMaterialCheckModel) => {
            const resultedMaterial = new MaterialCheckModel(
                result.Material,
                parseInt(result.Quantity, 10),
                result.StorageLoc,
            );
            resultedMaterial.updateFromResponse(result, longText);
            resultedMaterials.push(resultedMaterial);
        });
        return resultedMaterials;
    }
}
